export type UiLanguage = 'en' | 'ga';

let currentLanguage: UiLanguage | undefined;

const ROUTE_LANGUAGE_MAP: Readonly<Record<string, UiLanguage>> = {
  en: 'en',
  ie: 'ga',
  // Preserve existing Gaeilge links while /ie remains the canonical route.
  ga: 'ga',
};

function getBasePath(baseUrl: string): string {
  const pathname = new URL(baseUrl || '/', 'https://askjohny.local').pathname;
  return pathname === '/' ? '' : pathname.replace(/\/+$/, '');
}

export function getLanguageFromPathname(
  pathname: string,
  defaultLanguage: UiLanguage = 'en',
  baseUrl = '/',
): UiLanguage {
  const basePath = getBasePath(baseUrl);
  const isWithinBase = !basePath || pathname === basePath || pathname.startsWith(`${basePath}/`);
  if (!isWithinBase) {
    return defaultLanguage;
  }

  const relativePath = basePath ? pathname.slice(basePath.length) : pathname;
  const routeCode = relativePath.split('/').filter(Boolean)[0]?.toLowerCase();
  return (routeCode && ROUTE_LANGUAGE_MAP[routeCode]) || defaultLanguage;
}

export function replaceLanguageRoute(language: UiLanguage, baseUrl = '/'): void {
  if (typeof window === 'undefined') {
    return;
  }

  const basePath = getBasePath(baseUrl);
  const routeCode = language === 'ga' ? 'ie' : 'en';
  const pathname = `${basePath}/${routeCode}`;
  const nextUrl = `${pathname}${window.location.search}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

export function normalizeLanguage(languageCode?: string | null): UiLanguage {
  if (!languageCode) {
    return 'en';
  }

  const normalized = languageCode.trim().toLowerCase();
  if (normalized.startsWith('ga')) {
    return 'ga';
  }

  return 'en';
}

export function getCurrentLanguage(defaultLanguage: UiLanguage = 'en'): UiLanguage {
  if (currentLanguage) {
    return currentLanguage;
  }

  if (typeof window !== 'undefined') {
    currentLanguage = getLanguageFromPathname(
      window.location.pathname,
      defaultLanguage,
      import.meta.env.BASE_URL,
    );
    return currentLanguage;
  }

  currentLanguage = defaultLanguage;
  return currentLanguage;
}

export function setCurrentLanguage(language: UiLanguage): void {
  currentLanguage = language;

  if (typeof document !== 'undefined') {
    document.documentElement.lang = language;
  }
}
